import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filter, takeUntil } from 'rxjs/operators';

/**
 * Optional marker a dialog component can implement to keep ESC from closing it
 * (e.g. dialogs that must only be dismissed through an explicit confirm action).
 */
interface SuppressEscClose {
  readonly suppressEscClose: boolean;
}

/**
 * Restores "ESC closes the dialog" globally even though
 * `MAT_DIALOG_DEFAULT_OPTIONS` sets `disableClose: true` (which otherwise
 * disables both backdrop-close AND ESC-close).
 *
 * Net standardized behavior for every dialog, with zero per-call wiring:
 * - backdrop click  -> does NOT close (from the global default)
 * - ESC key         -> closes (restored here)
 * - explicit "X"    -> closes
 *
 * A dialog component may opt out by exposing `suppressEscClose === true`.
 */
@Injectable({ providedIn: 'root' })
export class DialogEscCloseService {
  private readonly dialog = inject(MatDialog);
  private initialized = false;

  /** Idempotently subscribes to newly opened dialogs. Called once at bootstrap. */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.dialog.afterOpened.subscribe((ref: MatDialogRef<unknown>) => {
      ref
        .keydownEvents()
        .pipe(
          filter((event) => event.key === 'Escape' && !event.defaultPrevented),
          takeUntil(ref.afterClosed()),
        )
        .subscribe(() => {
          const instance =
            ref.componentInstance as Partial<SuppressEscClose> | null;
          if (instance?.suppressEscClose === true) {
            return;
          }
          ref.close();
        });
    });
  }
}
